import { ButtonComponent } from '@/shared/components/button/button.component';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Menubar } from 'primeng/menubar';
import { filter, mergeMap, Observable, startWith, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/auth.service';
import { UserLogged } from '../../../../models/user/user-logged';
import { UserRepository } from '../../../../repositories/user/user.repository';

@Component({
  selector: 'app-header-menu-logged',
  standalone: true,
  imports: [AsyncPipe, ButtonModule, MenuModule, RouterLink, BadgeModule, TranslateModule, Menubar, ButtonComponent],
  templateUrl: './header-menu-logged.component.html',
  styleUrls: ['./header-menu-logged.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderMenuLoggedComponent implements OnInit {
  userAccessRoute = '';
  isAuthenticated$!: Observable<boolean>;
  itemsLogged$!: Observable<MenuItem[]>;
  userFullName = '';
  acronym = '';
  private readonly authService = inject(AuthService);
  private readonly translateService = inject(TranslateService);
  private readonly userRepository = inject(UserRepository);
  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
  );

  readonly isLoginPage = computed(() => this.currentUrl()?.startsWith('/login'));

  ngOnInit(): void {
    this.isAuthenticated$ = this.authService.isAuthenticated$();
    this.itemsLogged$ = this.translateService.onLangChange.asObservable().pipe(
      startWith(this.translateService.currentLang),
      mergeMap(() => this.userRepository.login()),
      tap((user: UserLogged) => {
        this.userFullName = `${user.firstName} ${user.lastName}`;
        this.acronym = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
        this.userAccessRoute = this.authService.hasAccess('client') ? 'admin' : 'my';
      }),
      map(() => [
        {
          label: this.userFullName,
          badge: this.acronym,
          badgeStyleClass: 'menu-badge-custom',
          items: [
            {
              label: this.translateService.instant('my.menu.logout'),
              icon: 'fas fa-lock-open',
              command: () => this.onLogout(),
            },
            {
              label: this.translateService.instant('my.menu.informations'),
              routerLink: [`${this.userAccessRoute}/informations`],
              icon: 'fas fa-user',
            },
          ],
        },
      ]),
    );
  }

  onLogout(): void {
    this.authService.logout();
  }
}
